import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import cx from 'classnames';
import { projectProcessFlagList } from 'lib/mapper';
import { timeline_current, timeline_complete } from 'components/base/images';
import { ProjectActions } from 'store/actionCreators';
import { useShallowSelector } from 'lib/utils';
import { debounce } from 'lodash';
import { isClinicUserSelector } from 'store/modules/user';

/**
 * Working Project TimeLine
 * @param {*} props
 * @param {number} stage // stage: 0 ~
 * @param {object} timeline // timeline: {create: 1630634289, preparation, scan, cad, cam, milling, postProcess, completed}, number | null
 */

const removedTimeline = 4;

const StageTimeline = props => {
  let {
    className = '',
    projectInfo,
    projectCode,
    ownerGroupCode,
    stage,
    timeline = {},
    partnerStage,
    partnerTimeline = {},
  } = props;
  const { user, excuteAppTimeline, hasAlarm, isClinicUser } = useShallowSelector(state => ({
    user: state.user.user,
    excuteAppTimeline: state.project.excuteAppTimeline,
    hasAlarm: state.project.hasAlarm,
    isClinicUser: isClinicUserSelector(state),
  }));

  const convertedTimeline = useMemo(() => {
    if (!timeline?.hasOwnProperty('new') || typeof stage !== 'number') return;
    const returnValue = Object.entries(timeline).reduce((acc, [key, value], index) => {
      let itemValue = value;
      if (value === null && index < stage) {
        itemValue = 'skip';
      }
      const currentItem = projectProcessFlagList.find(item => item.index === key);
      return acc.concat({
        stage: index,
        name: currentItem?.name,
        value: itemValue,
        stageIndex: currentItem?.index,
      });
    }, []);
    return returnValue;
  }, [timeline, stage]);

  const convertedPartnerTimeline = useMemo(() => {
    if (!timeline?.hasOwnProperty('new') || typeof partnerStage !== 'number') return;
    const returnValue = Object.entries(partnerTimeline).reduce((acc, [key, value], index) => {
      let itemValue = value;
      if (value === null && index < partnerStage) {
        itemValue = 'skip';
      }
      const currentItem = projectProcessFlagList.find(item => item.index === key);
      return acc.concat({
        stage: index,
        name: currentItem?.name,
        value: itemValue,
        stageIndex: currentItem?.index,
      });
    }, []);
    return returnValue;
  }, [partnerTimeline, partnerStage]);

  const isWithdraw = isClinicUser ? false : !projectInfo?.clientGroupCode;
  const isNew = !ownerGroupCode;
  const isOwner = isNew ? isNew : user?.groupCode === ownerGroupCode;
  const currentStage = isOwner ? stage : partnerStage;
  const currentTimeline = isOwner ? convertedTimeline : convertedPartnerTimeline;

  const partnerZidx = isOwner ? 11 : 12;
  const clinetZidx = isOwner ? 12 : 11;

  const handleEditTimeline = debounce(stageValue => {
    ProjectActions.edit_timeline_request({
      projectCode,
      stage: stageValue,
    });
  }, 500);

  useEffect(() => {
    // NOTE: App 실행 시, 변경 된 excuteAppTimeline State 값으로 Stage 변경
    if (projectCode === excuteAppTimeline?.projectCode && !!excuteAppTimeline?.progressFlag) {
      ProjectActions.edit_timeline_request({
        projectCode: excuteAppTimeline?.projectCode,
        stage: excuteAppTimeline?.progressFlag,
      });
    }
  }, [excuteAppTimeline]);

  return (
    <Styled.StageTimeline
      data-component-name="StageTimeline"
      className={className}
      partnerZidx={partnerZidx}
      clinetZidx={clinetZidx}
    >
      <div className="stageTimeline__wrapper">
        <div className="stageTimeline__container">
          <div className="stageTimeline__bg_box client">
            {convertedTimeline?.map((item, idx) => {
              if (item.stage === removedTimeline) return;
              return <StageTimelineBg key={idx} stage={stage} item={item} type="client" />;
            })}
          </div>
          <div className="stageTimeline__bg_box partner">
            {convertedPartnerTimeline?.map((item, idx) => {
              if (item.stage === removedTimeline) return;
              return <StageTimelineBg key={idx} stage={partnerStage} item={item} type="partner" />;
            })}
          </div>
          <div className="stageTimeline__bg_box step">
            {currentTimeline?.map((item, idx) => {
              const nullStatus = item.value === null;
              const proceededStatus = item.stage < currentStage;
              const skipStatus = item.value === 'skip';
              const currentStatus = item.stage === currentStage;
              const yetStatus = item.stage > currentStage;

              if (item.stage === removedTimeline) return;
              return (
                <div
                  key={idx}
                  className={cx(`stageTimeline__step`, {})}
                  onClick={() => {
                    if (isWithdraw) return;
                    if (isNew) return;
                    if (hasAlarm) return;
                    handleEditTimeline(item.stage);
                  }}
                >
                  <div className={cx('stageTimeline__step_label')}>{item.name}</div>
                  {!skipStatus && !yetStatus && !currentStatus && proceededStatus && (
                    <img src={timeline_complete} />
                  )}
                  {currentStatus && <img src={timeline_current} />}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Styled.StageTimeline>
  );
};

const StageTimelineBg = ({ item, stage, type }) => {
  const stageStatus =
    item.value === 'skip'
      ? 'skip'
      : item.stage < stage
      ? 'pass'
      : item.stage === stage
      ? 'curr'
      : item.stage > stage
      ? 'yet'
      : '';

  return (
    <Styled.StageTimelineBg className={cx(`stageTimelineBg ${item.stageIndex}`)}>
      <div
        className={cx(`stageTimelineBg__prev_icon ${type} ${stageStatus}`, {
          isHide: stageStatus === 'skip' || stageStatus === 'yet',
          none: item.stage === 0,
        })}
      ></div>
      <div className={cx(`stageTimelineBg__bar`, { none: item.stage === 0 })}>
        <div className={cx(`stageTimelineBg__bar_item ${type} ${stageStatus}`)}></div>
      </div>
      <div className={cx(`stageTimelineBg__icon ${type} ${stageStatus}`)}></div>
    </Styled.StageTimelineBg>
  );
};

export default React.memo(StageTimeline);

const Styled = {
  StageTimeline: styled.div`
    z-index: 100;
    .stageTimeline__wrapper {
      padding: 20px 36px;
      .stageTimeline__container {
        position: relative;
        /* width: 100%; */
        height: 47px;
        .stageTimeline__bg_box {
          position: absolute;
          bottom: 0;
          left: 0;
          display: flex;

          &.client {
            z-index: ${({ clinetZidx }) => clinetZidx};
          }
          &.partner {
            z-index: ${({ partnerZidx }) => partnerZidx};
          }
          &.step {
            z-index: ${({ clinetZidx }) => clinetZidx + 1};
            /* column-gap: 239px; */
            column-gap: 96px;
            .stageTimeline__step {
              width: 26px;
              height: 26px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              background-color: transparent;
              position: relative;
              .stageTimeline__step_label {
                position: absolute;
                top: -18px;
                left: 50%;
                transform: translate(-50%, 0%);
                font-size: 14px;
                font-weight: 400;
                color: #888;
                width: max-content;
              }
              &:hover {
                cursor: pointer;
              }
            }
          }
        }
      }
    }
  `,

  StageTimelineBg: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    .stageTimelineBg__prev_icon {
      margin-left: -26px;
      position: relative;
      width: 26px;
      height: 26px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 12;
      &.none {
        display: none;
      }
      &.isHide {
        visibility: hidden !important;
      }
      &.pass,
      &.curr {
        &.client {
          background-color: #25256f;
        }
        &.partner {
          background-color: #00bee2;
        }
      }
    }
    .stageTimelineBg__icon {
      position: relative;
      width: 26px;
      height: 26px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 11;
      &.new {
        &.client {
          background-color: #25256f;
          visibility: ${({ isOwner }) => (isOwner ? `visible` : `hidden`)};
        }
        &.partner {
          background-color: #00bee2;
          visibility: ${({ isOwner }) => (!isOwner ? `visible` : `hidden`)};
        }
      }

      &.pass,
      &.curr {
        &.client {
          background-color: #25256f;
        }
        &.partner {
          background-color: #00bee2;
        }
      }

      &.skip,
      &.yet {
        background-color: #d9d9d9;
      }
    }

    .stageTimelineBg__bar {
      position: relative;
      z-index: 10;
      /* width: 240px; */
      width: 97px;
      height: 8px;
      margin-left: -1px;
      &.none {
        display: none;
      }
      .stageTimelineBg__bar_item {
        position: relative;
        /* width: 242px; */
        width: 98px;
        height: 4px;

        &.client {
          margin-bottom: 4px;
        }
        &.partner {
          margin-top: 4px;
        }

        &.pass,
        &.curr {
          &.client {
            background-color: #25256f;
          }
          &.partner {
            background-color: #00bee2;
          }
        }

        &.skip,
        &.yet {
          background-color: #d9d9d9;
        }
      }
    }
  `,
};
